import React from "react";
import { BsStarFill, BsStar, BsPersonCircle } from "react-icons/bs";

function ReviewsTab({ reviews = [] }) {
  const averageRating =
    reviews.length > 0
      ? (
          reviews.reduce((total, review) => total + Number(review.rating || 0), 0) /
          reviews.length
        ).toFixed(1)
      : "0.0";

  const ratingCounts = [5, 4, 3, 2, 1].map((rating) => ({
    rating,
    count: reviews.filter((review) => Number(review.rating) === rating).length,
  }));

  const renderStars = (rating) => {
    const stars = [];

    for (let i = 1; i <= 5; i++) {
      stars.push(
        i <= rating ? (
          <BsStarFill key={i} />
        ) : (
          <BsStar key={i} />
        )
      );
    }

    return <span className="reviews-stars">{stars}</span>;
  };

  return (
    <div className="reviews-container">
      <style>{`
        .reviews-container {
          font-family: 'Raleway', sans-serif;
          color: #000000;
        }

        .reviews-header {
          margin-bottom: 1.5rem;
        }

        .reviews-header h2 {
          font-size: 1.5rem;
          font-weight: 800;
          margin-bottom: .35rem;
        }

        .reviews-header p {
          color: #666;
          margin: 0;
        }

        .reviews-summary {
          display: grid;
          grid-template-columns: 180px 1fr;
          gap: 2rem;
          padding: 1.5rem;
          background: #ffffff;
          border: 1px solid #eee;
          border-radius: 16px;
          margin-bottom: 1.5rem;
        }

        .rating-overview {
          text-align: center;
          padding: 1rem;
        }

        .average-rating {
          font-size: 3rem;
          font-weight: 800;
          line-height: 1;
          margin-bottom: .5rem;
        }

        .reviews-stars {
          color: #fff000;
          display: inline-flex;
          gap: 3px;
          font-size: 1.1rem;
        }

        .rating-overview p {
          color: #666;
          font-size: .85rem;
          margin-top: .5rem;
        }

        .rating-breakdown {
          display: flex;
          flex-direction: column;
          gap: .6rem;
          justify-content: center;
        }

        .rating-row {
          display: grid;
          grid-template-columns: 45px 1fr 35px;
          align-items: center;
          gap: .75rem;
          font-size: .85rem;
        }

        .rating-bar {
          height: 8px;
          background: #eee;
          border-radius: 20px;
          overflow: hidden;
        }

        .rating-bar-fill {
          height: 100%;
          background: #fff000;
          border-radius: 20px;
        }

        .reviews-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .review-card {
          background: #ffffff;
          border: 1px solid #eee;
          border-radius: 16px;
          padding: 1.25rem;
        }

        .review-top {
          display: flex;
          align-items: center;
          gap: .75rem;
          margin-bottom: .75rem;
        }

        .review-avatar {
          font-size: 2.2rem;
          color: #777;
        }

        .reviewer-name {
          font-weight: 700;
          margin: 0;
        }

        .review-date {
          color: #888;
          font-size: .75rem;
        }

        .review-rating {
          margin-bottom: .6rem;
        }

        .review-comment {
          color: #444;
          line-height: 1.6;
          margin: 0;
        }

        .empty-reviews {
          text-align: center;
          padding: 3rem 1.5rem;
          background: #ffffff;
          border: 1px solid #eee;
          border-radius: 16px;
        }

        .empty-star {
          font-size: 3rem;
          color: #fff000;
          margin-bottom: 1rem;
        }

        .empty-reviews h3 {
          font-weight: 800;
          margin-bottom: .5rem;
        }

        .empty-reviews p {
          color: #777;
          margin: 0;
        }

        @media (max-width: 768px) {
          .reviews-summary {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <div className="reviews-header">
        <h2>Reviews & Ratings</h2>
        <p>See what your clients think about your work.</p>
      </div>

      {reviews.length === 0 ? (
        <div className="empty-reviews">
          <div className="empty-star">
            <BsStarFill />
          </div>

          <h3>No reviews yet</h3>

          <p>
            You don't have any client reviews yet. Reviews will appear here
            after clients rate your completed jobs.
          </p>
        </div>
      ) : (
        <>
          <div className="reviews-summary">
            <div className="rating-overview">
              <div className="average-rating">{averageRating}</div>

              {renderStars(Math.round(Number(averageRating)))}

              <p>
                Based on {reviews.length}{" "}
                {reviews.length === 1 ? "review" : "reviews"}
              </p>
            </div>

            <div className="rating-breakdown">
              {ratingCounts.map((item) => {
                const percentage =
                  reviews.length > 0
                    ? (item.count / reviews.length) * 100
                    : 0;

                return (
                  <div className="rating-row" key={item.rating}>
                    <span>{item.rating} ★</span>

                    <div className="rating-bar">
                      <div
                        className="rating-bar-fill"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>

                    <span>{item.count}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="reviews-list">
            {reviews.map((review, index) => (
              <div
                className="review-card"
                key={review.id || index}
              >
                <div className="review-top">
                  <BsPersonCircle className="review-avatar" />

                  <div>
                    <p className="reviewer-name">
                      {review.reviewer ||
                        review.client_name ||
                        review.clientName ||
                        "Client"}
                    </p>

                    {review.location && (
                      <span className="review-date">
                        {review.location}
                      </span>
                    )}
                    {review.created_at && (
                      <span className="review-date">
                        {new Date(review.created_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>

                <div className="review-rating">
                  {renderStars(Number(review.rating || 0))}
                </div>

                <p className="review-comment">
                  {review.comment || "No comment provided."}
                </p>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default ReviewsTab;